import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useForm } from "react-hook-form";

import { resetForm } from "@/api/form-reset";
import { Spinner } from "@/common/components/spinner/spinner";
import { cancelPayment, confirmPayment } from "@/generated/api/users";

type Fields = {
  status: "CANCEL" | "CONFIRM" | "";
  date: string;
};

const Action = ({ paymentId }: { paymentId: number }) => {
  const confirmPayment = useConfirmPaymentMutation();
  const cancelPayment = useCancelPaymentMutation();
  const queryClient = useQueryClient();
  const form = useForm<Fields>({
    defaultValues: {
      date: DateTime.now().toISODate(),
    },
  });
  const isPending =
    confirmPayment.status === "pending" || cancelPayment.status === "pending";

  const handleSubmit = (data: Fields) => {
    if (data.status === "") {
      return;
    }

    if (!data.date && data.status === "CONFIRM") {
      alert(t`Vyplňte datum přijetí platby`);
      return;
    }

    if (data.status === "CANCEL") {
      cancelPayment.mutate(
        { paymentId },
        {
          onError: resetForm(form),
          onSuccess: () => {
            void queryClient.invalidateQueries();
          },
        },
      );
    } else {
      confirmPayment.mutate(
        { date: data.date, paymentId },
        {
          onError: resetForm(form),
          onSuccess: () => {
            void queryClient.invalidateQueries();
          },
        },
      );
    }
  };

  return (
    <form className="flex gap-2">
      <input {...form.register("date")} type="date" />
      <select
        {...form.register("status", {
          onChange: async () => {
            await form.handleSubmit(handleSubmit)();
          },
        })}
      >
        <option value="">
          <Trans>Akce</Trans>
        </option>
        <option value="CONFIRM">
          <Trans>Přijmout platbu</Trans>
        </option>
        <option value="CANCEL">
          <Trans>Zrušit platbu</Trans>
        </option>
      </select>
      {isPending ? <Spinner className="h-4! w-4!" withHint={false} /> : null}
    </form>
  );
};

const useConfirmPaymentMutation = () =>
  useMutation({
    mutationFn: ({ paymentId, date }: { paymentId: number; date: string }) =>
      confirmPayment(paymentId, { date }),
  });

const useCancelPaymentMutation = () =>
  useMutation({
    mutationFn: ({ paymentId }: { paymentId: number }) =>
      cancelPayment(paymentId),
  });

export { Action };
